/**
 * Campus filtering utilities
 *
 * Handles two URL strategies for campus filtering:
 * 1. `?zid=<base64-encoded-campus-ids>` — encoded campus IDs (e.g. ?zid=WkNBMDAwNw==)
 * 2. `/<campus-slug>` — human-readable campus name slug (e.g. /allen)
 *
 * The zid value is persisted in a cookie (30-day expiry) so it survives
 * the login/signup flow and subsequent visits without the query param.
 */

const ZID_COOKIE_NAME = 'zid';
const ZID_COOKIE_MAX_AGE = 2592000; // 30 days in seconds

// ─── Cookie helpers ───────────────────────────────────────────────

export const getZidFromCookie = (): string | null => {
  const cookies = document.cookie.split(';');
  const zidCookie = cookies.find(c => c.trim().startsWith(`${ZID_COOKIE_NAME}=`));
  return zidCookie ? zidCookie.split('=')[1].trim() : null;
};

export const saveZidToCookie = (zid: string): void => {
  document.cookie = `${ZID_COOKIE_NAME}=${zid}; path=/; max-age=${ZID_COOKIE_MAX_AGE}; SameSite=Lax`;
};

export const clearZidCookie = (): void => {
  document.cookie = `${ZID_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
};

// ─── Campus slug cookie (for /allen style URLs) ───────────────────

const CAMPUS_SLUG_COOKIE = 'campus_slug';

export const getCampusSlugFromCookie = (): string | null => {
  const cookies = document.cookie.split(';');
  const cookie = cookies.find(c => c.trim().startsWith(`${CAMPUS_SLUG_COOKIE}=`));
  return cookie ? decodeURIComponent(cookie.split('=')[1].trim()) : null;
};

export const saveCampusSlugToCookie = (slug: string): void => {
  document.cookie = `${CAMPUS_SLUG_COOKIE}=${encodeURIComponent(slug)}; path=/; max-age=${ZID_COOKIE_MAX_AGE}; SameSite=Lax`;
};

export const clearCampusSlugCookie = (): void => {
  document.cookie = `${CAMPUS_SLUG_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
};

// ─── Decode zid ───────────────────────────────────────────────────

/**
 * Decode a Base64-encoded, comma-separated list of campus IDs.
 * Falls back to the cookie value if `encodedString` is null.
 * Saves to cookie when found in URL params.
 *
 * @returns Array of campus IDs or null if none found
 */
export const decodeCampusIds = (encodedString: string | null): string[] | null => {
  if (!encodedString) {
    // Fallback: read from cookie
    const cookieValue = getZidFromCookie();
    if (!cookieValue) return null;
    encodedString = cookieValue;
  } else {
    // Persist for future visits
    saveZidToCookie(encodedString);
  }

  try {
    const decoded = atob(decodeURIComponent(encodedString));
    return decoded.split(',').map(id => id.trim()).filter(Boolean);
  } catch (error) {
    console.error('Error decoding campus IDs:', error);
    return null;
  }
};

// ─── Build Redis search query ─────────────────────────────────────

/**
 * Build a Redis search query that filters campuses by ID.
 * Returns '*' (all) when no filter is provided.
 */
export const buildCampusSearchQuery = (campusIds: string[] | null): string => {
  if (!campusIds || campusIds.length === 0) return '*';
  return `@id:(${campusIds.join('|')})`;
};

/**
 * Build a Redis search query that filters campuses by name (case-insensitive partial match).
 * Used for the /allen style slug fallback.
 */
export const buildCampusNameSearchQuery = (slug: string): string => {
  if (!slug) return '*';
  // Redis full-text search — the slug is used as a search term against the name field
  return `@name:*${slug.toLowerCase()}*`;
};
